// 強化：素材や銭・霊玉を使って育てる画面。装備（強化・限界突破・一括売却）・お守り（所持一覧・詳細・ロック・錬成・一括売却）・式神・建物図面。
// どのタブも1画面に収め、長い所持一覧だけ枠の中で動かす。付け替え（装備・お守り・道具・式神）は編成の装備のタブ。
// 式神タブ：30体の一覧と育成の詳細（式神Lv・霊玉で強化・★ごとのスキル。GAME_DESIGN 16.3）。
// PC は右の列に詳細、スマホは下の札（装備は下から出る札）。操作はサーバーへ送って /api/me を取り直す。
// お守りはランダム生成の個体（GAME_DESIGN 18章）。キャラごとに3枠（編成で付ける）。ロック中・装備中・購入品は錬成・売却の素材に選べない（サーバーでも弾く）。
import {
  BATTLE_CONFIG,
  META_CONFIG,
  omamoriCap,
  BUILDING_TYPES,
  LATE_BUILDING_TYPES,
  hardUnlocked,
  BUILD_ORDER,
  CHARS,
  CHAR_IDS,
  type CharId,
  inventoryCap,
  omamoriOwner,
  RARITIES,
  RARITY_LABEL,
  blueprintCost,
  blueprintCap,
  buildLoadout,
  fuseRarityWeights,
  omamoriBlock,
  omamoriFuseCost,
  omamoriScore,
  omamoriSellTargets,
  omamoriSellValue,
  omamoriKind,
  type EquipSlot,
  type GachaItem,
  type OmamoriItem,
  type Profile,
  type Rarity,
  SHIKIGAMI,
  SHIKIGAMI_IDS,
} from '@hyakki/core';
import { api, type ResourceKind } from '../api';
import { BUILDING_INFO, BUILDING_SPRITES, faceStyle, omikujiArt, OMK_ICON, spriteStyle, spriteFitStyle } from '../assets';
import { register } from '../router';
import { act, state, subscribe } from '../state';
import { RES_LABEL, esc, fmt, onAct, resIcon } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { omDetailHtml, omName, omThumbHtml, rarityLabel, subChipsHtml, subShort } from '../ui/omamori-view';
import { playOmikuji } from '../ui/omikuji-fx';
import { confirmDialog, openModal, toast } from '../ui/overlay';
import { errorText, notReadyHtml, runOp as run } from '../ui/status';
import { setSubTab } from '../ui/shell';
import { bindCompanionActions } from '../ui/companion';
import { bindShikiActions, openShikiSheet, ownedShiki, shikiDetailHtml, shikiListHtml, shikiTotalsHtml } from '../ui/shiki';
import { isMobileLayout } from '../ui/viewport';
import { lvBoxHtml, type LvRow } from '../ui/lvup';
import { AUTO_LABEL, SLOT_ICON, SLOT_LABEL, byRare, equippedBy, itemCellHtml, itemDetailHtml, runItemAction, sellDialog } from '../ui/equip-view';
import { tabTutorial } from '../tutorial';

const TABS = [
  { id: 'equip', label: '装備', icon: 'equip' },
  { id: 'omamori', label: 'お守り', icon: 'omamori' },
  { id: 'shiki', label: '式神', icon: 'shiki' },
  { id: 'blueprint', label: '建物図面', icon: 'blueprint' },
] as const;
type Tab = (typeof TABS)[number]['id'];
type Sort = 'score' | 'new' | 'kind';
type EqSort = 'rare' | 'level' | 'new';
const EQ_SORT: { id: EqSort; label: string }[] = [
  { id: 'rare', label: 'レア度' },
  { id: 'level', label: '強化値' },
  { id: 'new', label: '新着' },
];

let tab: Tab = 'equip';
const sel = {
  /** 装備タブで選んでいる装備・絞り込み・並び */
  item: null as number | null,
  eqSlot: 'all' as 'all' | EquipSlot,
  eqSort: 'rare' as EqSort,
  /** キャラで並べる：そのキャラが付けている装備 → 付けられる装備 → それ以外（null＝しない） */
  eqChar: null as CharId | null,
  om: null as number | null,
  bp: 'yagura' as string,
  /** 錬成：素材を選んでいる間は true */
  fuse: false,
  mats: [] as number[],
  filter: 'all' as 'all' | Rarity,
  sort: 'score' as Sort,
  /** 式神タブで選んでいる式神（PC の右の詳細） */
  shiki: null as string | null,
};

const BLOCK_LABEL = { locked: 'ロック中', equipped: '装備中', paid: '購入品' } as const;

register('enhance', {
  title: '強化',
  mount(el, params) {
    const t = params.get('tab');
    if (t && TABS.some((x) => x.id === t)) tab = t as Tab;
    const sid = params.get('shiki');
    if (sid && SHIKIGAMI[sid]) sel.shiki = sid;
    const iid = Number(params.get('item'));
    if (iid) sel.item = iid;
    el.innerHTML = `<div class="page fill enhance"><section class="panel-paper deco enh-panel">
      <header class="screen-head"><h1 class="screen-title">${artIcon('enhance')}強化</h1></header>
      <div class="seg-tabs enh-tabs" role="tablist">${TABS.map((x) => `<button role="tab" class="tab-btn" data-act="tab" data-tab="${x.id}">${icon(x.icon)}${x.label}</button>`).join('')}</div>
      <div class="tab-body"></div></section></div>`;
    const body = el.querySelector<HTMLElement>('.tab-body')!;
    const draw = () => {
      const p = state.profile;
      el.querySelectorAll<HTMLElement>('[data-tab]').forEach((b) => {
        b.classList.toggle('on', b.dataset.tab === tab);
        b.setAttribute('aria-selected', String(b.dataset.tab === tab));
      });
      body.className = `tab-body tb-${tab}`;
      setSubTab('enhance', tab);
      if (!p) return void (body.innerHTML = notReadyHtml('育成データ'));
      if (tab === 'equip') drawEquip(body, p);
      else if (tab === 'omamori') drawOmamori(body, p);
      else if (tab === 'shiki') drawShiki(body, p);
      else drawBlueprint(body, p);
    };
    draw();
    // タブの初訪問の案内（画面を離れた・別のタブにしたら出さない）
    let alive = true;
    const t0 = tab;
    tabTutorial('enhance', t0, () => alive && tab === t0);
    const off = subscribe((ch) => {
      if (ch.has('profile') || ch.has('resources')) draw();
    });
    const offAct = onAct(el, {
      tab: (b) => {
        tab = b.dataset.tab as Tab;
        sel.fuse = false;
        sel.mats = [];
        draw();
        const t = tab;
        tabTutorial('enhance', t, () => alive && tab === t);
      },
    });
    const handler = makeHandler(() => draw());
    el.addEventListener('click', handler);
    el.addEventListener('change', handler);
    const offShiki = bindShikiActions(el, () => draw());
    const offComp = bindCompanionActions(el, () => draw());
    // スマホで編成から「強化」で来たときは、その装備の札を開く
    if (tab === 'equip' && sel.item != null && isMobileLayout()) openItemSheet(() => draw());
    return () => {
      alive = false;
      itemSheet?.close();
      off();
      offAct();
      offShiki();
      offComp();
      el.removeEventListener('click', handler);
      el.removeEventListener('change', handler);
    };
  },
});

function makeHandler(redraw: () => void) {
  return async (e: Event) => {
    const t = e.target as HTMLElement;
    const p = state.profile;
    if (!p) return;
    if (e.type === 'change') {
      const s = t.closest<HTMLSelectElement>('[data-sort]');
      if (s) {
        sel.sort = s.value as Sort;
        redraw();
      }
      return;
    }
    const btn = t.closest<HTMLButtonElement>('button');
    if (btn?.disabled) return;
    // 式神：PC は右の詳細を切り替え、スマホは詳細の札（式神Lv・霊玉で強化・★ごとのスキル）
    const sk = t.closest<HTMLElement>('[data-shiki]');
    if (sk && tab === 'shiki') {
      const id = sk.dataset.shiki!;
      sel.shiki = id;
      if (isMobileLayout()) openShikiSheet(id, redraw);
      return redraw();
    }
    const cell = t.closest<HTMLElement>('[data-item]');
    if (cell && tab === 'equip') {
      sel.item = Number(cell.dataset.item);
      redraw();
      if (isMobileLayout()) openItemSheet(redraw);
      return;
    }
    const flt = t.closest<HTMLElement>('[data-filter]');
    if (flt) {
      sel.filter = flt.dataset.filter as typeof sel.filter;
      return redraw();
    }
    const om = t.closest<HTMLElement>('[data-om]');
    if (om) {
      const id = Number(om.dataset.om);
      if (sel.fuse) {
        const o = p.omamori.find((x) => x.id === id);
        if (!o) return;
        const b = omamoriBlock(p, o);
        if (b) return toast(`${BLOCK_LABEL[b]}のお守りは錬成の素材にできません`, 'error');
        if (sel.mats.includes(id)) sel.mats = sel.mats.filter((x) => x !== id);
        else if (sel.mats.length < 3) sel.mats.push(id);
        else return toast('素材は3つまでです', 'info');
      }
      sel.om = id;
      return redraw();
    }
    const bp = t.closest<HTMLElement>('[data-bp]');
    if (bp) {
      sel.bp = bp.dataset.bp!;
      return redraw();
    }
    const a = t.closest<HTMLElement>('[data-a]')?.dataset.a;
    if (!a) return;
    if (btn) btn.disabled = true;
    try {
      switch (a) {
        case 'eqslot':
          sel.eqSlot = (t.closest<HTMLElement>('[data-v]')?.dataset.v ?? 'all') as typeof sel.eqSlot;
          redraw();
          break;
        case 'eqsort':
          sel.eqSort = (t.closest<HTMLElement>('[data-v]')?.dataset.v ?? 'rare') as EqSort;
          redraw();
          break;
        case 'eqchar': {
          // 同じ顔をもう一度押すと絞り込みを外す
          const c = t.closest<HTMLElement>('[data-v]')?.dataset.v as CharId | undefined;
          sel.eqChar = !c || c === sel.eqChar ? null : c;
          redraw();
          break;
        }
        case 'eqbulk':
          sellDialog(p);
          break;
        case 'close':
          sel.item = null;
          redraw();
          break;
        case 'enhance':
        case 'enhance10':
        case 'merge':
        case 'lock':
        case 'sell': {
          const it = p.items.find((x) => x.id === sel.item);
          if (it) await runItemAction(a, p, it, equippedBy(p, it.id) ?? state.oshi);
          break;
        }
        case 'omlock': {
          const o = p.omamori.find((x) => x.id === sel.om);
          if (o) await run(api.lockOmamori(o.id, !o.locked), o.locked ? 'ロックを外しました' : 'ロックしました');
          break;
        }
        case 'omsell': {
          const o = p.omamori.find((x) => x.id === sel.om);
          if (!o) break;
          const v = omamoriSellValue(o);
          const warn = o.rarity === 'ssr' || o.rarity === 'sr' ? `\n${rarityLabel(o.rarity)}のお守りです。` : '';
          if (!(await confirmDialog(`「${omName(o.defId)}」（${rarityLabel(o.rarity)}）を霊玉${fmt(v)}で売ります。${warn}`, { ok: '売る', danger: true, title: '売却' }))) break;
          await run(api.sellOmamori([o.id]), '売りました', 'reward');
          break;
        }
        case 'fusemode':
          sel.fuse = !sel.fuse;
          sel.mats = [];
          redraw();
          break;
        case 'fuseclear':
          sel.mats = [];
          redraw();
          break;
        case 'fuse':
          await doFuse(p);
          break;
        case 'bulksell':
          openBulkSell(p);
          break;
        case 'bpup':
          await run(api.upgradeBlueprint(sel.bp as never), '図面を強化しました', 'enhanceBig');
          break;
      }
    } finally {
      if (btn?.isConnected) btn.disabled = false;
    }
  };
}

// ---------- 装備（強化・限界突破・売却。付け替えは編成） ----------
/** スマホで開いている装備の札 */
let itemSheet: { body: HTMLElement; close: () => void } | null = null;

function drawEquip(body: HTMLElement, p: Profile) {
  let list = p.items.filter((i) => sel.eqSlot === 'all' || i.slot === sel.eqSlot);
  if (sel.eqSort === 'new') list = list.sort((a, b) => b.id - a.id);
  else if (sel.eqSort === 'level') list = list.sort((a, b) => b.level - a.level || byRare(a, b));
  else list = list.sort(byRare);
  // キャラで絞り込む（そのキャラが装備できるものだけ。装備中のものを前へ、並びの中で安定に）
  const ec = sel.eqChar;
  if (ec) {
    const rank = (i: (typeof list)[number]) => (equippedBy(p, i.id) === ec ? 0 : !i.charId || i.charId === ec ? 1 : 2);
    list = list.map((i, k) => ({ i, k, r: rank(i) })).filter((x) => x.r < 2).sort((a, b) => a.r - b.r || a.k - b.k).map((x) => x.i);
  }
  if (sel.item != null && !p.items.some((x) => x.id === sel.item)) sel.item = null;
  if (sel.item == null && !isMobileLayout()) sel.item = list[0]?.id ?? null;
  const it = sel.item != null ? p.items.find((x) => x.id === sel.item) : undefined;
  const cap = inventoryCap(p);
  body.innerHTML = `
    <div class="enh-split eqf-split">
      <div class="enh-main eqf-main">
        <div class="eqf-bar">
          <h3 class="sec-title" aria-label="所持装備 ${p.items.length}/${cap}" title="所持装備">${icon('equip')}<small class="${p.items.length >= cap ? 'bad' : ''}">${p.items.length}/${cap}</small></h3>
          ${(p.settings?.autoSell ?? 'none') === 'none' ? '' : `<span class="eq-auto muted small" title="自動売却（拾った時に銭へ）">${icon('coin')}自動売却 ${AUTO_LABEL[p.settings!.autoSell as keyof typeof AUTO_LABEL]}</span>`}
          <button class="btn small" data-a="eqbulk">${icon('coin')}一括売却</button>
        </div>
        <div class="eq-filter" role="toolbar" aria-label="並び替えと絞り込み">
          <span class="eq-slots-f eq-sort-f" role="group" aria-label="並び替え">${icon('swap')}${EQ_SORT.map((x) => `<button class="eq-chip ${sel.eqSort === x.id ? 'on' : ''}" data-a="eqsort" data-v="${x.id}" aria-pressed="${sel.eqSort === x.id}">${x.label}</button>`).join('')}</span>
          <span class="eq-slots-f eq-char-f" role="group" aria-label="キャラで絞り込み">${CHAR_IDS.map((c) => `<button class="eq-chip eq-face ${ec === c ? 'on' : ''}" data-a="eqchar" data-v="${c}" aria-pressed="${ec === c}" aria-label="${esc(CHARS[c].name)}"><span class="by-face inline" style="${faceStyle(c)}"></span></button>`).join('')}</span>
          <span class="eq-slots-f" role="group" aria-label="部位">${(['all', 'weapon', 'armor', 'netsuke'] as const)
            .map((k) => `<button class="eq-chip ${sel.eqSlot === k ? 'on' : ''}" data-a="eqslot" data-v="${k}" aria-pressed="${sel.eqSlot === k}" ${k === 'netsuke' ? `aria-label="${SLOT_LABEL[k]}"` : ''}>${k === 'all' ? '全部' : k === 'netsuke' ? icon(SLOT_ICON[k]) : SLOT_LABEL[k]}</button>`)
            .join('')}</span>
        </div>
        <ul class="inv-grid fit-scroll">${list.map((i) => itemCellHtml(p, i, { sel: i.id === sel.item })).join('') || `<li class="muted small inv-empty">${p.items.length ? '条件に合う装備がありません' : '装備がありません。合戦で手に入ります。'}</li>`}</ul>
      </div>
      ${it && !isMobileLayout() ? `<aside class="enh-detail sheet-card item-detail">${itemDetailHtml(p, it, equippedBy(p, it.id) ?? state.oshi, 'forge')}</aside>` : ''}
    </div>`;
  if (itemSheet) {
    if (it) itemSheet.body.innerHTML = itemDetailHtml(p, it, equippedBy(p, it.id) ?? state.oshi, 'forge');
    else itemSheet.close();
  }
}

/** スマホ：装備の詳細は下から出る札 */
function openItemSheet(redraw: () => void) {
  const p = state.profile;
  const it = p?.items.find((x) => x.id === sel.item);
  if (!p || !it) return;
  const html = itemDetailHtml(p, it, equippedBy(p, it.id) ?? state.oshi, 'forge');
  if (itemSheet) return void (itemSheet.body.innerHTML = html);
  const m = openModal(html, {
    className: 'sheet-wrap',
    onClose: () => {
      itemSheet = null;
      sel.item = null;
    },
  });
  itemSheet = { body: m.body, close: m.close };
  m.body.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-a]');
    const q = state.profile;
    const x = q?.items.find((y) => y.id === sel.item);
    if (!b || b.disabled || !q || !x) return;
    if (b.dataset.a === 'close') return m.close();
    b.disabled = true;
    await runItemAction(b.dataset.a!, q, x, equippedBy(q, x.id) ?? state.oshi);
    if (b.isConnected) b.disabled = false;
    redraw();
  });
}

// ---------- お守り（所持一覧・詳細・ロック・錬成・一括売却。付けるのは編成） ----------
const sorted = (p: Profile) => {
  const list = p.omamori.filter((o) => sel.filter === 'all' || o.rarity === sel.filter);
  if (sel.sort === 'new') return list.sort((a, b) => b.id - a.id);
  if (sel.sort === 'kind') return list.sort((a, b) => omamoriKind(a.defId).localeCompare(omamoriKind(b.defId)) || omamoriScore(b) - omamoriScore(a));
  return list.sort((a, b) => omamoriScore(b) - omamoriScore(a) || b.id - a.id);
};

function drawOmamori(body: HTMLElement, p: Profile) {
  const byId = new Map(p.omamori.map((o) => [o.id, o]));
  if (sel.om != null && !byId.has(sel.om)) sel.om = null;
  sel.mats = sel.mats.filter((id) => byId.has(id) && !omamoriBlock(p, byId.get(id)!));
  if (sel.om == null) sel.om = sorted(p)[0]?.id ?? null;
  const o = sel.om != null ? byId.get(sel.om) : undefined;
  const list = sorted(p);
  const counts = Object.fromEntries(RARITIES.map((r) => [r, p.omamori.filter((x) => x.rarity === r).length]));
  body.innerHTML = `
    <div class="enh-split om2 ${sel.fuse ? 'fusing' : ''}">
      <div class="enh-main om2-main om2-noslot">
        <div class="om2-bar">
          <div class="om2-filter" role="group" aria-label="レア度">${(['all', ...RARITIES] as const)
            .map((r) => `<button class="chip ${sel.filter === r ? 'on' : ''}" data-filter="${r}">${r === 'all' ? `すべて<small class="${p.omamori.length >= omamoriCap() ? 'bad' : ''}">${p.omamori.length}/${omamoriCap()}</small>` : `${RARITY_LABEL[r]}<small>${counts[r]}</small>`}</button>`)
            .join('')}</div>
          <select class="om2-sort" data-sort aria-label="並べ替え">${(
            [
              ['score', '強い順'],
              ['new', '新しい順'],
              ['kind', '主効果順'],
            ] as const
          )
            .map(([v, l]) => `<option value="${v}" ${sel.sort === v ? 'selected' : ''}>${l}</option>`)
            .join('')}</select>
          <button class="btn small" data-a="fusemode">${sel.fuse ? '錬成をやめる' : '錬成'}</button>
          <button class="btn small" data-a="bulksell" ${sel.fuse ? 'disabled' : ''}>一括売却</button>
        </div>
        <ul class="om2-grid fit-scroll">${
          list
            .map((x) => {
              const by = omamoriOwner(p, x.id);
              const blk = omamoriBlock(p, x);
              const mi = sel.mats.indexOf(x.id);
              const cls = [x.id === sel.om ? 'sel' : '', sel.fuse && blk ? 'blocked' : '', mi >= 0 ? 'mat' : ''].join(' ');
              return `<li><button class="om2-cell ${cls}" data-om="${x.id}" ${sel.fuse && blk ? `aria-disabled="true" title="${BLOCK_LABEL[blk]}は素材にできません"` : ''}>${omThumbHtml(x, { size: 's' })}
                <span class="om2-cell-t"><b>${esc(omName(x.defId))}</b>${subChipsHtml(x.subs)}</span>
                ${by ? `<span class="by-face" style="${faceStyle(by)}" aria-label="${esc(CHARS[by].name)}が装備中"></span>` : ''}${x.locked ? `<i class="om2-lock" aria-label="ロック中">${icon('lock')}</i>` : ''}${sel.fuse && blk ? `<em class="om2-blk">${BLOCK_LABEL[blk]}</em>` : ''}${mi >= 0 ? `<em class="om2-mi">${mi + 1}</em>` : ''}</button></li>`;
            })
            .join('') || '<li class="muted small inv-empty">まだありません。おまもりガチャ（毎日1回無料）・章ボスの初回撃破・星報酬・図鑑で手に入ります。</li>'
        }</ul>
      </div>
      <aside class="enh-detail sheet-card om-detail om2-detail">${sel.fuse ? fusePanel(p) : o ? detailPanel(p, o) : `<div class="state-box">${icon('omamori')}<p>お守りを選んでください</p></div>`}</aside>
    </div>`;
}

function detailPanel(p: Profile, o: OmamoriItem) {
  const by = omamoriOwner(p, o.id);
  const blk = omamoriBlock(p, o);
  return `${omDetailHtml(o)}
    <p class="ed-note">${o.paid ? '購入品（完成品として引き継ぎ）。売却・錬成はできません' : by ? `<span class="by-face inline" style="${faceStyle(by)}"></span>${esc(CHARS[by].name)}が装備中：売却・錬成の素材にできません` : o.locked ? 'ロック中：売却・錬成・自動売却（所持の上限）の対象になりません' : `所持が${omamoriCap()}個になると、拾うたびに弱いお守りから自動で霊玉に換わります。残したいものはロック`}</p>
    <div class="ed-actions om2-actions">
      <div class="om2-row">
        <button class="btn ${o.locked ? 'primary' : ''}" data-a="omlock" ${o.paid ? 'disabled' : ''}>${icon(o.locked ? 'lock' : 'unlock')}${o.locked ? 'ロック解除' : 'ロック'}</button>
        <button class="btn" data-a="omsell" ${blk ? 'disabled' : ''}><span>売却</span><small>${resIcon('reidama')}${fmt(omamoriSellValue(o))}</small></button>
      </div>
    </div>`;
}

function fusePanel(p: Profile) {
  const mats = sel.mats.map((id) => p.omamori.find((x) => x.id === id)!).filter(Boolean);
  const cost = omamoriFuseCost(p);
  const w = mats.length === 3 ? fuseRarityWeights(mats.map((m) => m.rarity)) : null;
  const tot = w ? RARITIES.reduce((a, r) => a + w[r], 0) : 1;
  const enough = cost.ticket || (state.resources.zeni ?? 0) >= cost.zeni;
  return `<header class="ed-head"><span class="om2-fuse-ic" style="${omikujiArt.icon(OMK_ICON.fuse)}"></span><div class="ed-name"><h2>錬成</h2><small class="muted">要らないお守り3つ＋銭で新しいお守りを1つ</small></div></header>
    <ol class="om2-mats">${[0, 1, 2]
      .map((i) => {
        const m = mats[i];
        return `<li>${m ? `${omThumbHtml(m, { size: 's' })}<span><b>${esc(omName(m.defId))}</b><small>${rarityLabel(m.rarity)}</small></span>` : `<span class="om-art empty">${i + 1}</span><span class="muted small">一覧から選ぶ</span>`}</li>`;
      })
      .join('')}</ol>
    <p class="ed-note">主効果は素材3つのどれか。上のレア度が出やすい。ロック中・装備中・購入品は選べません</p>
    ${w ? `<p class="om2-odds">${RARITIES.filter((r) => w[r] > 0).map((r) => `<span class="r-${r}">${RARITY_LABEL[r]} ${Math.round((w[r] / tot) * 100)}%</span>`).join('')}</p>` : ''}
    <div class="ed-actions">
      <p class="om2-cost">${cost.ticket ? `<i style="${omikujiArt.icon(OMK_ICON.ofuda)}"></i>錬成札 1枚（残り${p.tickets?.fuse ?? 0}）` : `<i class="ri ri-zeni"></i>銭 ${fmt(cost.zeni)}`}</p>
      <button class="btn primary big wide" data-a="fuse" ${mats.length === 3 && enough ? '' : 'disabled'}>錬成する</button>
      <button class="btn small" data-a="fuseclear" ${mats.length ? '' : 'disabled'}>選び直す</button>
    </div>`;
}

async function doFuse(p: Profile) {
  const mats = sel.mats.map((id) => p.omamori.find((x) => x.id === id)).filter((x): x is OmamoriItem => !!x);
  if (mats.length !== 3) return toast('素材を3つ選んでください', 'error');
  const cost = omamoriFuseCost(p);
  const ok = await new Promise<boolean>((resolve) => {
    let done = false;
    const m = openModal(
      `<p class="confirm-text">この3つを使って錬成します。<b class="om2-lose">素材は無くなります。</b></p>
       <ul class="om2-confirm">${mats.map((x) => `<li class="r-${x.rarity}">${omThumbHtml(x, { size: 'm' })}<b>${esc(omName(x.defId))}</b><small>${rarityLabel(x.rarity)}・${esc(x.subs.map(subShort).join('・') || '副効果なし')}</small></li>`).join('')}</ul>
       <p class="muted small">${cost.ticket ? '錬成札を1枚使います' : `銭 ${fmt(cost.zeni)} を使います`}</p>
       <div class="btn-row"><button class="btn" data-no>やめる</button><button class="btn primary" data-yes>錬成する</button></div>`,
      { title: '錬成の確認', onClose: () => !done && resolve(false) },
    );
    m.body.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-yes]')) (done = true), resolve(true), m.close();
      else if (t.closest('[data-no]')) m.close();
    });
  });
  if (!ok) return;
  const nonce = `fuse-${crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
  try {
    const r = await act(api.fuseOmamori(mats.map((x) => x.id), nonce));
    const id = r.item?.id ?? r.itemId;
    const made = state.profile?.omamori.find((x) => x.id === id);
    sel.mats = [];
    sel.fuse = false;
    if (made) {
      sel.om = made.id;
      const item: GachaItem = { kind: 'omamori', fortune: 'kichi', defId: made.defId, omamori: { defId: made.defId, rarity: made.rarity, q: made.q, subs: made.subs } };
      await playOmikuji([item], { fuse: true });
    }
  } catch (e) {
    toast(errorText(e), 'error');
  }
}

function openBulkSell(p: Profile) {
  let max: Rarity = 'n';
  const m = openModal('', { title: '一括売却' });
  const draw = () => {
    const items = omamoriSellTargets(p, max);
    const v = items.reduce((a, o) => a + omamoriSellValue(o), 0);
    m.body.innerHTML = `<p class="muted small">ロック中・装備中・購入品は売りません。</p>
      <div class="om2-bulk"><span>レア度</span><div class="seg">${(['n', 'r', 'sr'] as const).map((r) => `<button class="chip ${max === r ? 'on' : ''}" data-max="${r}">${RARITY_LABEL[r]}まで</button>`).join('')}</div>
      <p class="om2-bulk-sum"><b>${items.length}</b>個 → ${resIcon('reidama')}霊玉 <b>${fmt(v)}</b></p>
      <div class="btn-row"><button class="btn" data-close>やめる</button><button class="btn danger" data-go ${items.length ? '' : 'disabled'}>売る</button></div>`;
  };
  draw();
  m.body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const mx = t.closest<HTMLElement>('[data-max]');
    if (mx) return (max = mx.dataset.max as Rarity), draw();
    if (t.closest('[data-go]')) {
      m.close();
      await run(api.sellOmamoriFilter(max), '売りました', 'reward');
    }
  });
}


// ---------- 建物図面 ----------
/** 図面Lv で変わるもの：建物のHP・効果（Lvごとに+10%）と、Lv5からの射程+1（battle の buildingStats と同じ式） */
function bpRows(t: (typeof BUILDING_TYPES)[number], lv: number): LvRow[] {
  const d = BATTLE_CONFIG.buildings[t];
  const m = (l: number) => 1 + BATTLE_CONFIG.buildLvStep * l;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const rows: LvRow[] = [];
  const row = (label: string, f: (l: number) => string) => rows.push({ label, before: f(lv), after: f(lv + 1) });
  if (d.hp > 1) row('建物のHP', (l) => `基本の${r1(m(l) * 100)}%`);
  if (d.dmg) row('威力', (l) => `持ち主の攻撃の${r1(d.dmg! * m(l) * 100)}%`);
  else if (d.aura) row('隣のマスの与ダメ', (l) => `+${r1(d.aura! * m(l) * 100)}%`);
  else if (d.heal) row('そばの回復', (l) => `最大HPの${r1(d.heal! * m(l) * 100)}%`);
  else if (d.repair) row('本陣の修復', (l) => `最大HPの${r1(d.repair! * m(l) * 100)}%`);
  if (d.range) row('射程', (l) => `${d.range! + (l >= 5 ? 1 : 0)}マス`);
  return rows;
}


/** 図面の Lv の上限（Lv の帯の長さ） */
const BP_MAX = META_CONFIG.blueprint.maxLevel;

function drawBlueprint(body: HTMLElement, p: Profile) {
  // 櫓・竹柵・幟は最初から。ほかは図面を Lv1 にすると建てられる（全キャラ共通）
  const unlocked = new Set(CHAR_IDS.flatMap((c) => buildLoadout(p, c).build.unlocked));
  const isOpen = (t: string) => unlocked.has(t as never);
  void BUILD_ORDER;
  if (!BUILDING_TYPES.includes(sel.bp as never)) sel.bp = 'yagura';
  const t = sel.bp as (typeof BUILDING_TYPES)[number];
  const info = BUILDING_INFO[t];
  const lv = p.blueprints[t] ?? 0;
  const sp = BUILDING_SPRITES[t];
  const open = isOpen(t);
  const cost = blueprintCost(lv) as Record<string, number>;
  const enough = Object.entries(cost).every(([k, v]) => (state.resources[k as 'zeni'] ?? 0) >= v);
  // 図面は里の鍛冶屋の Lv まで
  const capped = lv < BP_MAX && lv >= blueprintCap(p);
  // 鉄砲狭間などは本編（第100陣）クリアで図面を作れる
  const late = LATE_BUILDING_TYPES.includes(t) && !hardUnlocked(p);
  body.innerHTML = `
    <div class="enh-split bp-split">
      <ul class="bp-list fit-scroll">${BUILDING_TYPES.map((x) => {
        const s = BUILDING_SPRITES[x];
        const o = isOpen(x);
        const l = p.blueprints[x] ?? 0;
        return `<li><button class="bp-row ${x === t ? 'sel' : ''} ${o ? '' : 'locked'} ${l >= BP_MAX ? 'max' : ''}" data-bp="${x}" aria-label="${BUILDING_INFO[x].name} ${o ? `Lv${l}` : '未習得'}">
          <span class="bp-tile-art" style="${s ? spriteStyle(s) + spriteFitStyle(s) : ''}"></span>${o ? `<em class="bp-lv">${l >= BP_MAX ? 'MAX' : `Lv${l}`}</em>` : `<em class="bp-lock">${icon('lock')}</em>`}
          <b>${BUILDING_INFO[x].name}</b></button></li>`;
      }).join('')}</ul>
      <div class="bp-art"><span class="bp-img" style="${sp ? spriteStyle(sp) + spriteFitStyle(sp) : ''}"></span></div>
      <aside class="enh-detail sheet-card bp-detail">
        <header class="ed-head"><span class="bp-thumb"><i style="${sp ? spriteStyle(sp) + spriteFitStyle(sp) : ''}"></i></span><div class="ed-name"><h2>${info.name}</h2><b class="ed-lv">Lv.${lv} <span class="arrow">≫</span> <span class="good">Lv.${lv + 1}</span></b></div></header>
        <p class="ed-desc clamp2">${esc(info.desc)}</p>
        ${open || capped || late ? '' : `<p class="ed-note">${icon('lock')}Lv1にすると建てられるようになります</p>`}
        ${lvBoxHtml('', bpRows(t, lv), !open ? '建てられるようになります' : lv === 4 && BATTLE_CONFIG.buildings[t].range ? '射程が1マス伸びます' : '')}
        <div class="ed-actions">
          <div class="mini-kv" ${capped || late ? 'hidden' : ''}>${Object.entries(cost)
            .filter(([k]) => RES_LABEL[k as ResourceKind])
            .map(([k, v]) => `<span><i class="ri ri-${k}"></i>${RES_LABEL[k as ResourceKind]}<b class="${(state.resources[k as 'zeni'] ?? 0) >= v ? '' : 'bad'}">${fmt(v)}</b></span>`)
            .join('')}</div>
          ${late
            ? `<button class="btn primary big wide bp-smith" disabled aria-label="本編クリアで習得できます">${icon('lock')}本編クリア後</button>`
            : capped
            ? `<button class="btn primary big wide bp-smith" disabled aria-label="里の鍛冶屋 Lv.${lv + 1}で強化できます">${icon('lock')}鍛冶屋Lv${lv + 1}</button>`
            : `<button class="btn primary big wide" data-a="bpup" ${enough ? '' : 'disabled'}>${icon(open ? 'enhance' : 'blueprint')}${open ? '強化' : '習得'}</button>`}
        </div>
      </aside>
    </div>`;
}

// ---------- 式神（式神Lv・限界突破のスキル。GAME_DESIGN 16.3） ----------
function drawShiki(body: HTMLElement, p: Profile) {
  const owned = ownedShiki(p);
  if (!sel.shiki || !SHIKIGAMI[sel.shiki]) sel.shiki = owned[0]?.[0] ?? null;
  body.innerHTML = `<div class="enh-split sk-split">
    <div class="sk-main">
      <div class="sk-top"><span class="sk-count">式神<b>${owned.length}</b>/${SHIKIGAMI_IDS.length}</span><span class="enh-chip sk-wallet"><i class="ri ri-reidama"></i>霊玉<b>${fmt(state.resources.reidama ?? 0)}</b></span></div>
      <div class="sk-sum2">${shikiTotalsHtml(p)}</div>
      <div class="sk-list fit-scroll">${shikiListHtml(p, sel.shiki)}</div>
    </div>
    <aside class="enh-detail skd-panel">${sel.shiki ? shikiDetailHtml(p, sel.shiki) : `<div class="state-box">${icon('shiki')}<p>式神を選んでください</p></div>`}</aside>
  </div>`;
}

